import React from 'react'

import { wrapCreateBrowserRouterV7 } from '@sentry/react'
import { useSelector } from 'react-redux'
import { createBrowserRouter, Navigate, Outlet, ScrollRestoration, useLocation } from 'react-router-dom'

import { lazyWithVersionCheck, useFullLoadWhenStale } from '@libs/typescript-utils'

import { useGTMPageTracking, useHubSpotChat } from '@hooks'

import { DashboardRoutes, OAuthRoutes } from '@components/routes'
import { PageLoadingErrorView, PageLoadingView } from '@components/ui'

import { ROUTES } from '@constants/routes'

import { RootErrorBoundary } from './RootErrorBoundary'

const AsyncLoginPage = lazyWithVersionCheck(() => import('@oldComponents/pages/LoginPage'), PageLoadingErrorView)
const AsyncSignupPage = lazyWithVersionCheck(() => import('@oldComponents/pages/SignupPage'), PageLoadingErrorView)
const AsyncResetPasswordPage = lazyWithVersionCheck(
  () => import('@oldComponents/pages/ResetPasswordPage'),
  PageLoadingErrorView
)
const AsyncResetPasswordConfirmPage = lazyWithVersionCheck(
  () => import('@oldComponents/pages/ResetPasswordConfirmPage'),
  PageLoadingErrorView
)
const AsyncNotFoundPublicPage = lazyWithVersionCheck(
  () => import('@components/pages/NotFoundPage/NotFoundPublicPage'),
  PageLoadingErrorView
)
const AsyncInviteRegistrationPage = lazyWithVersionCheck(
  () => import('@oldComponents/pages/InviteRegistrationPage'),
  PageLoadingErrorView
)
const AsyncOnboardingPage = lazyWithVersionCheck(
  () => import('@oldComponents/pages/OnboardingPage'),
  PageLoadingErrorView
)
const AsyncDownloadsPage = lazyWithVersionCheck(
  () => import('@oldComponents/pages/DownloadsPage'),
  PageLoadingErrorView
)
const AsyncOnboardingInvitationPage = lazyWithVersionCheck(
  () => import('@oldComponents/pages/OnboardingInvitationPage'),
  PageLoadingErrorView
)

function MainRouter() {
  useGTMPageTracking()
  useHubSpotChat()
  useFullLoadWhenStale()

  return (
    <>
      <ScrollRestoration />
      <RootErrorBoundary>
        <React.Suspense fallback={<PageLoadingView />}>
          <Outlet />
        </React.Suspense>
      </RootErrorBoundary>
    </>
  )
}

function PrivateRouteGuard() {
  const location = useLocation()
  const isLoggedIn = useSelector((state: Store) => state.auth.isLoggedIn)
  const isLogoutRequested = useSelector((state: Store) => state.auth.isLogoutRequested)

  if (isLoggedIn) {
    return <Outlet />
  }

  let state
  if (!isLogoutRequested) {
    state = { redirectToAfterLogin: location.pathname + location.search }
  }

  return <Navigate to={ROUTES.login} replace state={state} />
}

/** Keeps the query string, in case the entry flow ever arrives with one. */
function LegacyOnboardingRedirect() {
  const { search } = useLocation()

  return <Navigate to={{ pathname: ROUTES.szamlazzEntry, search }} replace />
}

function PublicRouteGuard() {
  const { state } = useLocation<{ redirectToAfterLogin?: string }>()

  const redirectUrl = state?.redirectToAfterLogin || ROUTES.root
  const isLoggedIn = useSelector((state: Store) => state.auth.isLoggedIn)

  if (isLoggedIn) {
    return <Navigate to={redirectUrl} replace />
  } else {
    return <Outlet />
  }
}

const sentryCreateBrowserRouter = wrapCreateBrowserRouterV7(createBrowserRouter)

export default sentryCreateBrowserRouter([
  {
    element: <MainRouter />,
    children: [
      {
        path: '/login',
        element: <PublicRouteGuard />,
        children: [
          {
            path: '/login',
            element: <AsyncLoginPage />,
          },
        ],
      },
      {
        path: '/signup',
        element: <PublicRouteGuard />,
        children: [
          {
            path: '/signup',
            element: <AsyncSignupPage />,
          },
        ],
      },
      {
        path: '/reset-password',
        element: <PublicRouteGuard />,
        children: [
          {
            path: '/reset-password',
            element: <AsyncResetPasswordPage />,
          },
        ],
      },
      {
        path: '/reset-password-confirm/:uid/:token',
        element: <PublicRouteGuard />,
        children: [
          {
            path: '/reset-password-confirm/:uid/:token',
            element: <AsyncResetPasswordConfirmPage />,
          },
        ],
      },
      {
        path: '/invite/:hash',
        element: <AsyncInviteRegistrationPage />,
      },
      {
        path: '/download/:hash',
        element: <AsyncDownloadsPage />,
      },
      {
        path: '/szamlazz/entry',
        element: <AsyncOnboardingPage />,
      },
      {
        path: '/szamlazz/entry/:company_id/invitation-status',
        element: <PrivateRouteGuard />,
        children: [
          {
            path: '/szamlazz/entry/:company_id/invitation-status',
            element: <AsyncOnboardingInvitationPage />,
          },
        ],
      },
      {
        path: '/szamlazz/entry/:company_id/check',
        element: <AsyncOnboardingPage />,
      },
      {
        path: '/szamlazz/entry/:company_id/kszi',
        element: <AsyncOnboardingPage />,
      },
      {
        // Szamlazz.hu sent users here until the entry flow moved; a redirect covers the links and open
        // tabs still pointing at the old path
        path: '/onboarding',
        element: <LegacyOnboardingRedirect />,
      },
      {
        path: '/dashboard/*',
        element: <PrivateRouteGuard />,
        children: [
          {
            path: '*',
            element: <DashboardRoutes />,
          },
        ],
      },
      {
        path: '/oauth/*',
        element: <PrivateRouteGuard />,
        children: [
          {
            path: '*',
            element: <OAuthRoutes />,
          },
        ],
      },
      {
        path: '/',
        element: <Navigate to={ROUTES.root} replace />,
      },
      {
        path: ROUTES.notFound,
        element: <AsyncNotFoundPublicPage />,
      },
      {
        path: '*',
        element: <AsyncNotFoundPublicPage />,
      },
    ],
  },
])
